import React, { useEffect, useState } from 'react';

const AccountCheckerHeader = ({ onSearch, isLoading, initialValue = '' }) => { // Added initialValue prop
  const [inputValue, setInputValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);

  // Sync input value if the parent's query changes (e.g., from history click)
  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      onSearch(inputValue.trim());
    }
  };

  return (
    <div className="mb-8 transform-gpu transition-medium hover:scale-[1.01]">
      <div className="relative overflow-hidden bg-gradient-to-r from-dark-500 to-dark-400 rounded-2xl p-6 md:p-8 border border-dark-400 hover:border-accent-green/30 transition-medium group">
        {/* ... (rest of the gradient/background elements remain the same) ... */}
         <div className="absolute -right-20 -top-20 w-64 h-64 bg-accent-green opacity-5 rounded-full blur-xl group-hover:opacity-10 group-hover:blur-2xl transition-all duration-500"></div>
         <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-accent-blue opacity-5 rounded-full blur-xl group-hover:opacity-10 group-hover:blur-2xl transition-all duration-500"></div>

        <div className="flex flex-col md:flex-row items-center justify-between z-10 relative">
          <div className="w-full md:w-1/2 mb-6 md:mb-0">
             {/* ... (title and description remain the same) ... */}
             <div className="text-sm font-medium text-accent-green mb-2 animate-pulse-slow">
                ACCOUNT CHECKER
             </div>
             <h2 className="text-3xl font-bold mb-3 group-hover:text-white transition-medium">
                Minecraft <span className="gradient-text inline-block ml-2 group-hover:animate-pulse-text">Credential Finder</span>
             </h2>
             <p className="text-gray-400 mb-6 text-sm max-w-md group-hover:text-gray-300 transition-medium">
                Enter a Minecraft username to search for matching credentials in our comprehensive database of leaked accounts.
             </p>
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Enter Minecraft username"
                  value={inputValue} // Use internal state
                  onChange={(e) => setInputValue(e.target.value)} // Update internal state
                  className={`w-full bg-dark-600 rounded-xl py-3 px-4 pl-10 text-sm focus:outline-none transition-all duration-300 ${
                    isFocused
                      ? 'ring-2 ring-accent-green/50 border-accent-green'
                      : 'border border-dark-400 hover:border-dark-300'
                  }`}
                  disabled={isLoading}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                />
                {/* ... (icon remains the same) ... */}
                 <i className={`fas fa-user absolute left-3 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${
                    isFocused ? 'text-accent-green' : 'text-gray-400'
                  }`}></i>
              </div>
              <button
                type="submit"
                className={`bg-gradient-to-r from-accent-green to-accent-blue text-dark-800 px-6 py-3 rounded-xl ${
                  isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90 hover:shadow-glow'
                } transition font-medium whitespace-nowrap flex items-center justify-center transform-gpu hover:-translate-y-0.5 active:translate-y-0`}
                disabled={isLoading}
              >
                {/* ... (button text logic remains the same) ... */}
                {isLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i> Searching...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search mr-2 group-hover:scale-110 transition-medium"></i>
                    <span className="group-hover:translate-x-1 transition-medium">Check Account</span>
                  </>
                )}
              </button>
            </form>
          </div>
          {/* ... (right side graphic remains the same) ... */}
          <div className="w-full md:w-1/2 md:absolute md:right-0 md:top-0 md:h-full overflow-hidden flex items-center justify-center opacity-80 group-hover:opacity-100 transition-medium">
             {/* ... (floating cards and shield graphic) ... */}
             <div className="relative animate-float transform-gpu">
                {/* Enhanced floating account cards with more animation */}
                <div className="absolute top-0 -left-32 transform rotate-12 shadow-lg animate-float delay-500">
                    <div className="w-48 h-16 bg-dark-600 rounded-lg p-2 flex items-center border border-dark-400 hover:border-accent-green transition-medium group/card">
                    <div className="w-12 h-12 bg-dark-500 rounded mr-2 flex items-center justify-center group-hover/card:rotate-6 transition-medium">
                        <i className="fas fa-user text-accent-green group-hover/card:scale-110 transition-medium"></i>
                    </div>
                    <div className="text-xs">
                        <div className="font-mono text-accent-green group-hover/card:text-white transition-medium">Steve</div>
                        <div className="text-gray-400 group-hover/card:text-gray-300 transition-medium">********</div>
                    </div>
                    </div>
                </div>
                <div className="absolute -top-12 left-24 transform -rotate-6 shadow-lg animate-float delay-700">
                    <div className="w-48 h-16 bg-dark-600 rounded-lg p-2 flex items-center border border-dark-400 hover:border-accent-blue transition-medium group/card">
                    <div className="w-12 h-12 bg-dark-500 rounded mr-2 flex items-center justify-center group-hover/card:-rotate-6 transition-medium">
                        <i className="fas fa-user text-accent-blue group-hover/card:scale-110 transition-medium"></i>
                    </div>
                    <div className="text-xs">
                        <div className="font-mono text-accent-blue group-hover/card:text-white transition-medium">Alex</div>
                        <div className="text-gray-400 group-hover/card:text-gray-300 transition-medium">********</div>
                    </div>
                    </div>
                </div>
                <div className="absolute top-16 left-10 transform rotate-3 shadow-lg animate-float delay-300">
                    <div className="w-48 h-16 bg-dark-600 rounded-lg p-2 flex items-center border border-dark-400 hover:border-accent-purple transition-medium group/card">
                    <div className="w-12 h-12 bg-dark-500 rounded mr-2 flex items-center justify-center group-hover/card:rotate-12 transition-medium">
                        <i className="fas fa-user text-accent-purple group-hover/card:scale-110 transition-medium"></i>
                    </div>
                    <div className="text-xs">
                        <div className="font-mono text-accent-purple group-hover/card:text-white transition-medium">Notch</div>
                        <div className="text-gray-400 group-hover/card:text-gray-300 transition-medium">********</div>
                    </div>
                    </div>
                </div>

                {/* Center shield with pulse animation */}
                <div className="relative w-32 h-32 bg-dark-500 rounded-xl flex items-center justify-center z-10 shadow-lg border border-dark-300 hover:border-accent-green transition-medium">
                    <div className="absolute inset-0 rounded-xl border-2 border-accent-green/30 animate-shield-pulse"></div>
                    <i className="fas fa-user-shield text-5xl text-accent-green group-hover:scale-110 transition-medium"></i>

                    {/* Particles */}
                    <div className="absolute inset-0 overflow-hidden rounded-xl">
                    <div className="particle-1"></div>
                    <div className="particle-2"></div>
                    <div className="particle-3"></div>
                    </div>
                </div>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};


export default AccountCheckerHeader;